// statistics_clinic_08a_practice_skewness.md — 3 DIY cases, each giving a
// mean/median/SD summary where the raw mean-median gap is misleading and only
// Pearson's second coefficient (SK2) settles whether the distribution is
// symmetric enough to treat as roughly normal.

export const STATS_CLINIC_08A_TRACKER_ID = 'statistics-clinic-08a-practice-bank';

export const STATS_CLINIC_08A_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_08A_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Support ticket handling times',
    scenario: 'A service desk manager wants to publish a "typical handling time" on the team dashboard. Most tickets close quickly, but a handful of escalated cases run for hours. The manager has asked whether the mean is a fair number to publish.',
    tables: [{
      columns: ['dataset', 'mean', 'median', 'sd'],
      rows: [['Ticket handling time (minutes)', '50', '38', '24']],
    }],
    answerGuide: {
      business_question: 'Is the mean a fair "typical handling time" to publish, or is the distribution too skewed for it?',
      statistic_family: 'Distribution shape.',
      statistic_chosen: "Pearson's second coefficient of skewness (SK2).",
      why_statistic: 'The 12-minute gap between mean and median means nothing until it is compared to the 24-minute spread.',
      calculation: 'SK2 = 3 x (50 - 38) / 24 = 36 / 24 = 1.5.',
      result: 'SK2 = 1.5, which is above 1 and positive, so the data is strongly right-skewed.',
      meaning: 'A long right tail of escalated tickets is dragging the mean above the median; most tickets actually close nearer 38 minutes than 50.',
      decision: 'Publish the median (38 minutes) as the typical handling time, with the IQR for spread, rather than the mean of 50.',
      caution: 'Because the distribution is strongly skewed it is not normal, so the 68-95-99.7 rule and the +/- 3 SD outlier fence do not apply — use the IQR fence instead.',
    },
    ruleTerms: {
      business_question: ['typical', 'mean', 'fair'],
      statistic_family: ['distribution shape', 'shape', 'symmetry'],
      statistic_chosen: ['sk2', 'pearson', 'skewness'],
      why_statistic: ['spread', 'standard deviation', 'sd', 'relative'],
      calculation: ['1.5', '36'],
      result: ['1.5', 'right', 'skew'],
      meaning: ['right tail', 'drag', 'above the median', 'escalated'],
      decision: ['median', '38'],
      caution: ['not normal', 'iqr', '68-95-99.7', '3 sd'],
    },
    criticalTerms: { calculation: ['1.5'], result: ['1.5'], decision: ['38'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: Manufacturing part weights',
    scenario: 'A quality engineer wants to set control limits at three standard deviations either side of the average part weight, which assumes a roughly normal distribution. Before doing so they need to confirm the weights are symmetric enough for that assumption to hold.',
    tables: [{
      columns: ['dataset', 'mean', 'median', 'sd'],
      rows: [['Part weight (grams)', '250.4', '250.0', '6.0']],
    }],
    answerGuide: {
      business_question: 'Are part weights symmetric enough to justify +/- 3 SD control limits?',
      statistic_family: 'Distribution shape.',
      statistic_chosen: "Pearson's second coefficient of skewness (SK2).",
      why_statistic: 'Control limits built on the mean and SD assume a roughly symmetric distribution, so the symmetry has to be measured rather than assumed.',
      calculation: 'SK2 = 3 x (250.4 - 250.0) / 6.0 = 1.2 / 6.0 = 0.2.',
      result: 'SK2 = 0.2, which is below 0.5, so the distribution is roughly symmetric.',
      meaning: 'The mean and median sit within about a fifth of a standard deviation of each other, so no meaningful tail is pulling the mean away from the centre.',
      decision: 'Proceed with the mean, SD and +/- 3 SD control limits — the normal toolkit is safe for this measure.',
      caution: 'Symmetry is only half of normality; also check excess kurtosis for heavy tails, and plot a histogram before locking the limits in.',
    },
    ruleTerms: {
      business_question: ['symmetric', 'control limit', '3 sd'],
      statistic_family: ['distribution shape', 'shape', 'symmetry'],
      statistic_chosen: ['sk2', 'pearson', 'skewness'],
      why_statistic: ['assume', 'symmetric', 'mean and sd'],
      calculation: ['0.2', '6.0', '6'],
      result: ['0.2', 'symmetric', 'below 0.5'],
      meaning: ['fifth of a standard deviation', 'no meaningful tail', 'close', 'centre'],
      decision: ['proceed', 'control limit', 'safe', 'mean'],
      caution: ['kurtosis', 'half', 'histogram', 'tails'],
    },
    criticalTerms: { calculation: ['0.2'], result: ['0.2'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: API response times',
    scenario: 'A platform team reports average API response time to stakeholders each month. Someone has pointed out that the mean and median differ by only three tenths of a second and argued that the difference is too small to worry about.',
    tables: [{
      columns: ['dataset', 'mean', 'median', 'sd'],
      rows: [['API response time (seconds)', '1.9', '1.6', '0.75']],
    }],
    answerGuide: {
      business_question: 'Is a 0.3-second gap between mean and median small enough to ignore when reporting average response time?',
      statistic_family: 'Distribution shape.',
      statistic_chosen: "Pearson's second coefficient of skewness (SK2).",
      why_statistic: 'A raw gap cannot be judged as big or small on its own — it only means something once divided by the standard deviation.',
      calculation: 'SK2 = 3 x (1.9 - 1.6) / 0.75 = 0.9 / 0.75 = 1.2.',
      result: 'SK2 = 1.2, above 1 and positive, so the distribution is strongly right-skewed.',
      meaning: 'The 0.3-second gap sounds trivial but the spread is small too, so relative to the spread a long right tail of slow calls is clearly pulling the mean up.',
      decision: 'Report the median (1.6 s) as the typical response time and add a P95 for the slow tail, instead of the mean of 1.9 s.',
      caution: 'Never judge a mean-median gap in raw units — the same 0.3-second gap would be negligible on a measure with a much larger standard deviation.',
    },
    ruleTerms: {
      business_question: ['gap', 'ignore', 'small enough', 'average'],
      statistic_family: ['distribution shape', 'shape', 'symmetry'],
      statistic_chosen: ['sk2', 'pearson', 'skewness'],
      why_statistic: ['raw gap', 'divided', 'standard deviation', 'relative'],
      calculation: ['1.2', '0.75'],
      result: ['1.2', 'right', 'skew'],
      meaning: ['small spread', 'relative', 'right tail', 'slow'],
      decision: ['median', '1.6', 'p95'],
      caution: ['raw units', 'never judge', 'standard deviation'],
    },
    criticalTerms: { calculation: ['1.2'], result: ['1.2'], decision: ['1.6'] },
  },
];

export const STATS_CLINIC_08A_CONFIG = {
  trackerId: STATS_CLINIC_08A_TRACKER_ID,
  resourceKey: 'statistics_clinic_08a_practice_skewness',
  rubricKey: 'statistics_skewness',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Skewness Cases',
  description: 'Each case gives you a mean, median and standard deviation. Standardise the mean-median gap with SK2 = 3 x (mean - median) / SD before deciding whether the distribution is symmetric enough to treat as roughly normal. Your work autosaves to your own practice record.',
  introAlert: 'Mean, median and mode are never exactly equal in real data. Judge the gap relative to the spread: |SK2| under 0.5 is roughly symmetric, over 1 is strongly skewed.',
  lenses: STATS_CLINIC_08A_LENSES,
  workingField: null,
  items: STATS_CLINIC_08A_ITEMS,
};
